/**
 * @file SkuBarcodeFields.tsx
 * @description GTIN и ТН ВЭД в диалоге фасовки (phase19.22, W17-2/W17-7,
 * ui-design §0 п.4, решение §9 п.5).
 *
 * GTIN — обычное поле, видно всегда: штрихкод производителя сканируют на
 * складе независимо от «Честного знака». Обязательным (вместе с ТН ВЭД) он
 * становится, только если категория подлежит маркировке — как было до фазы.
 * Контрольная цифра проверяется на клиенте (`gtinError`), чтобы опечатку
 * видно было сразу, а не после 400 `sku_gtin_invalid`.
 */

import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { gtinError } from "../../../../lib/product-kind-format";

export interface SkuBarcodeValue {
  gtin: string;
  tnved: string;
}

interface Props {
  value: SkuBarcodeValue;
  onChange: (patch: Partial<SkuBarcodeValue>) => void;
  /** Категория подлежит «Честному знаку» — GTIN и ТН ВЭД обязательны. */
  markingRequired: boolean;
  tnvedHint: string | null;
  /** GTIN до правки: неизменённый старый код не проверяем (как сервер). */
  originalGtin: string | null;
  disabled?: boolean;
}

export function SkuBarcodeFields({
  value,
  onChange,
  markingRequired,
  tnvedHint,
  originalGtin,
  disabled,
}: Props) {
  const changed = value.gtin.trim() !== (originalGtin ?? "").trim();
  const gtinProblem = changed ? gtinError(value.gtin) : null;
  return (
    <div>
      <div className="label-editorial mb-3">
        {markingRequired ? "Маркировка ЧЗ (категория подлежит)" : "Штрихкод производителя"}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="sku-gtin">GTIN{markingRequired ? " *" : ""}</Label>
          <Input
            id="sku-gtin"
            inputMode="numeric"
            value={value.gtin}
            onChange={(e) => onChange({ gtin: e.target.value })}
            disabled={disabled}
            required={markingRequired}
            aria-invalid={gtinProblem ? true : undefined}
            placeholder="4600000000000"
            className="font-mono"
          />
          <p className={gtinProblem ? "text-xs text-destructive" : "text-xs text-on-surface-variant"}>
            {gtinProblem ?? "8, 12, 13 или 14 цифр со штрихкода на упаковке"}
          </p>
        </div>
        {markingRequired ? (
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="sku-tnved">ТН ВЭД *</Label>
            <Input
              id="sku-tnved"
              value={value.tnved}
              onChange={(e) => onChange({ tnved: e.target.value })}
              disabled={disabled}
              required
            />
            <p className="text-xs text-on-surface-variant">
              {tnvedHint ? `Подсказка: ${tnvedHint}` : "10 цифр"}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
